import React, {FC, useEffect, useState} from "react";
import {message, Popconfirm, Tag, Tooltip} from "antd";
import {WarningOutlined} from "@ant-design/icons";
import {useMutation} from "@apollo/client";
import i18n from "../../services/i18n";
import {RESOLVE_SURPLUS_MOLLIE_PAYMENTS} from "../../containers/payments/scheduledPayments.graphql";

/**
 * #1069 — Badge "Doppelzahlung" an einer Rate, für die eine weitere Mollie-Zahlung
 * eingegangen ist, obwohl sie bereits bezahlt war (Server-Feld
 * `ScheduledPayment.surplusMolliePaymentCount`). Die Erstattung ist ein manueller Schritt;
 * Betrag und Mollie-ID stehen in der Zahlungs-Historie der Rate.
 *
 * #1141 — Mit `scheduledPaymentId` ist das Badge klickbar: nach Rückfrage wird die
 * Doppelzahlung als erledigt markiert (nur ein Audit-Eintrag, keine Geldbewegung) und das
 * Badge ausgeblendet. Ohne ID bleibt es ein reiner Hinweis.
 */
type SurplusMolliePaymentBadgeProps = {
    count?: number | null;
    scheduledPaymentId?: string;
    // Nach dem Markieren die Liste neu laden: der Server liefert dann 0, die Zeile rendert
    // kein Badge mehr, und eine spätere weitere Zahlung erscheint als frisches Badge.
    onResolved?: () => void;
};

const renderBadge = (count: number, clickable: boolean) => (
    <Tooltip title={i18n.containers.payments.PaymentsListColumns.surplusPaymentBadgeTooltip({count: String(count)})}>
        <Tag color="red" icon={<WarningOutlined />} style={{margin: 0, cursor: clickable ? "pointer" : undefined}} data-testid="SurplusMolliePaymentBadge">
            {i18n.containers.payments.PaymentsListColumns.surplusPaymentBadge()}
        </Tag>
    </Tooltip>
);

// Eigene Komponente, damit der Mutation-Hook (und damit der Apollo-Kontext) nur dort
// gebraucht wird, wo tatsächlich ein klickbares Badge steht.
const ResolvableSurplusBadge: FC<{count: number; scheduledPaymentId: string; onResolved?: () => void}> = ({count, scheduledPaymentId, onResolved}) => {
    // Nur Überbrückung bis zum Neuladen der Liste; ändert sich die Anzahl vom Server
    // (z. B. weitere Zahlung), gilt wieder der Server-Wert.
    const [resolved, setResolved] = useState(false);

    useEffect(() => {
        setResolved(false);
    }, [count]);

    const [resolveSurplus, {loading}] = useMutation(RESOLVE_SURPLUS_MOLLIE_PAYMENTS, {
        onCompleted: () => {
            setResolved(true);
            message.success(i18n.containers.payments.PaymentsListColumns.surplusPaymentResolved(), 3);
            if (onResolved) {
                onResolved();
            }
        },
        onError: (error) => {
            message.error(error.message, 5);
        },
    });

    if (resolved) {
        return null;
    }

    return (
        <Popconfirm
            title={i18n.containers.payments.PaymentsListColumns.surplusPaymentResolveConfirm()}
            okText={i18n.containers.payments.PaymentsListColumns.surplusPaymentResolveOk()}
            cancelText={i18n.containers.payments.PaymentsListColumns.surplusPaymentResolveCancel()}
            okButtonProps={{loading}}
            onConfirm={() => resolveSurplus({variables: {scheduledPaymentId}})}
        >
            {renderBadge(count, true)}
        </Popconfirm>
    );
};

const SurplusMolliePaymentBadge: FC<SurplusMolliePaymentBadgeProps> = ({count, scheduledPaymentId, onResolved}) => {
    if (!count || count <= 0) {
        return null;
    }

    return scheduledPaymentId ? <ResolvableSurplusBadge count={count} scheduledPaymentId={scheduledPaymentId} onResolved={onResolved} /> : renderBadge(count, false);
};

export default SurplusMolliePaymentBadge;
